import { computed, ref } from 'vue'

import type { Ref, ComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'

import { SortKanbanBy, SortKanbanDirection } from '../enums'

interface FilterSortOptions {
  initialSortBy: string | null
  initialSortDirection: string | null
}

interface FilterSortReturn {
  options: NativeSelectOption[]
  query: Ref<string | null>
  sortBy: ComputedRef<string | null>
  sortDirection: ComputedRef<string | null>
  handleChange: (option: NativeSelectOption) => void
}

const sortList: { value: string, text: string, sortBy: string, sortDirection: string | null }[] = [
  {
    value: SortKanbanBy.StageUpdatedAt,
    text: 'By update',
    sortBy: SortKanbanBy.StageUpdatedAt,
    sortDirection: null,
  },
  {
    value: `${SortKanbanBy.StageDeadline}:${SortKanbanDirection.ASC}`,
    text: 'By deadline',
    sortBy: SortKanbanBy.StageDeadline,
    sortDirection: SortKanbanDirection.ASC,
  },
  {
    value: SortKanbanBy.KanbanPriority,
    text: 'By priority',
    sortBy: SortKanbanBy.KanbanPriority,
    sortDirection: null,
  },
]

const buildSortValue = (sortBy: string | null, sortDirection: string | null): string | null => {
  if (!sortBy) return null

  return sortList.find(item => item.sortBy === sortBy && item.sortDirection === sortDirection)?.value
    ?? sortList.find(item => item.sortBy === sortBy)?.value
    ?? null
}

export const useFilterSort = ({ initialSortBy, initialSortDirection }: FilterSortOptions): FilterSortReturn => {
  const query = ref<string | null>(buildSortValue(initialSortBy, initialSortDirection))

  const options: NativeSelectOption[] = sortList.map(item => ({
    id: item.value,
    value: item.value,
    text: item.text,
    selected: item.value === query.value,
  }))

  const sortBy = computed<string | null>(() => {
    if (!query.value) return null

    return sortList.find(item => item.value === query.value)?.sortBy ?? null
  })

  const sortDirection = computed<string | null>(() => {
    if (!query.value) return null

    return sortList.find(item => item.value === query.value)?.sortDirection ?? null
  })

  const handleChange = (option: NativeSelectOption): void => {
    query.value = String(option.value)
  }

  return {
    options,
    query,
    sortBy,
    sortDirection,
    handleChange,
  }
}
